package playground.gradient.ui

import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.geometry.Size
import androidx.compose.ui.graphics.LinearGradientShader
import androidx.compose.ui.graphics.RadialGradientShader
import androidx.compose.ui.graphics.Shader
import androidx.compose.ui.graphics.ShaderBrush
import androidx.compose.ui.graphics.SweepGradientShader
import playground.gradient.data.ColorStop
import playground.gradient.toHex
import kotlin.math.min


fun createLinearShaderBrush(
    colorStops: List<ColorStop>,
    start: Offset,
    end: Offset,
): ShaderBrush {
    return object : ShaderBrush() {
        override fun createShader(size: Size): Shader {
            return LinearGradientShader(
                from = Offset(start.x * size.width, start.y * size.height),
                to = Offset(end.x * size.width, end.y * size.height),
                colors = colorStops.sortedBy { it.stop }.map { it.color },
                colorStops = colorStops.sortedBy { it.stop }.map {
                    it.stop
                },
            )
        }

    }
}

fun generateLinearGradientCode(
    colorStops: List<ColorStop>,
    start: Offset,
    end: Offset,
): String {

    //
    val fromX = start.x
    val fromY = start.y
    val toX = end.x
    val toY = end.y
    val colors = colorStops.toListOfColors()
    val stops = colorStops.toListOfStops()
    //


    return """
        val linearGradient = object : ShaderBrush() {
            override fun createShader(size: Size): Shader {
                return LinearGradientShader(
                    from = Offset(${fromX}f * size.width, ${fromY}f * size.height),
                    to = Offset(${toX}f * size.width, ${toY}f * size.height),
                    colors = listOf(
                        $colors
                    ),
                    colorStops = listOf(
                        $stops
                    ),
                )
            }
        }
    """.trimIndent()

}

fun createRadialShaderBrush(
    colorStops: List<ColorStop>,
    center: Offset,
    radius: Float,
): ShaderBrush {
    return object : ShaderBrush() {
        override fun createShader(size: Size): Shader {
            val length = min(size.width, size.height)
            return RadialGradientShader(
                center = Offset(center.x * size.width, center.y * size.height),
                radius = radius * length,
                colors = colorStops.sortedBy { it.stop }.map { it.color },
                colorStops = colorStops.sortedBy { it.stop }.map {
                    it.stop
                },
            )
        }

    }
}

fun generateRadialGradientCode(
    colorStops: List<ColorStop>,
    center: Offset,
    radius: Float,
): String {

    val centerX = center.x
    val centerY = center.y
    val colors = colorStops.toListOfColors()
    val stops = colorStops.toListOfStops()


    return """
        val radialGradient = object : ShaderBrush() {
            override fun createShader(size: Size): Shader {
                
                val length = min(size.width, size.height)
                return RadialGradientShader(
                    center = Offset(${centerX}f * size.width, ${centerY}f * size.height),
                    radius = ${radius}f * length,
                    colors = listOf(
                        $colors
                    ),
                    colorStops = listOf(
                        $stops
                    ),
                )
            }
        }
    """
}


fun createSweepShaderBrush(
    colorStops: List<ColorStop>,
    center: Offset,
): ShaderBrush {
    return object : ShaderBrush() {
        override fun createShader(size: Size): Shader {
            return SweepGradientShader(
                center = Offset(center.x * size.width, center.y * size.height),
                colors = colorStops.sortedBy { it.stop }.map { it.color },
                colorStops = colorStops.sortedBy { it.stop }.map {
                    it.stop
                },
            )
        }

    }
}

fun generateSweepGradientCode(
    colorStops: List<ColorStop>,
    center: Offset,
): String {

    val centerX = center.x
    val centerY = center.y
    val colors = colorStops.toListOfColors()
    val stops = colorStops.toListOfStops()

    return """
        val sweepGradient = object : ShaderBrush() {
            override fun createShader(size: Size): Shader {
                
                return SweepGradientShader(
                    center = Offset(${centerX}f * size.width, ${centerY}f * size.height),
                    colors = listOf(
                        $colors
                    ),
                    colorStops = listOf(
                        $stops
                    ),
                )
            }
        }
    """
}

private fun List<ColorStop>.toListOfColors(): String {
    return sortedBy { it.stop }
        .map { it.color.toHex() }
        .joinToString(",\n") { "Color($it)" }
}

private fun List<ColorStop>.toListOfStops(): String {
    return sortedBy { it.stop }
        .map { it.stop }
        .joinToString(",\n") { "${it}f" }
}

